import { useState, useCallback } from 'react'
import type { ProgressState } from '@/types'

const STORAGE_KEY = 'partner-training-progress'
const TOTAL_MODULES = 5

function loadProgress(): ProgressState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return { completed: [] }
    return JSON.parse(raw) as ProgressState
  } catch {
    return { completed: [] }
  }
}

function saveProgress(state: ProgressState) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state))
  } catch {
    // localStorage unavailable — silent fail
  }
}

export function useProgress() {
  const [state, setState] = useState<ProgressState>(loadProgress)

  const markComplete = useCallback((id: string) => {
    setState(prev => {
      if (prev.completed.includes(id)) return prev
      const next: ProgressState = {
        completed: [...prev.completed, id],
        lastVisited: id,
      }
      saveProgress(next)
      return next
    })
  }, [])

  const markIncomplete = useCallback((id: string) => {
    setState(prev => {
      const next: ProgressState = {
        ...prev,
        completed: prev.completed.filter(c => c !== id),
      }
      saveProgress(next)
      return next
    })
  }, [])

  const isComplete = useCallback(
    (id: string) => state.completed.includes(id),
    [state.completed],
  )

  const resetAll = useCallback(() => {
    const next: ProgressState = { completed: [] }
    saveProgress(next)
    setState(next)
  }, [])

  const progressPercent = Math.round((state.completed.length / TOTAL_MODULES) * 100)

  return {
    completed: state.completed,
    lastVisited: state.lastVisited,
    markComplete,
    markIncomplete,
    isComplete,
    resetAll,
    progressPercent,
    completedCount: state.completed.length,
    totalCount: TOTAL_MODULES,
  }
}
